import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from 'react';
import { Dimensions, InteractionManager, type View } from 'react-native';
import { useAnimatedReaction } from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';
import { useInstanceId, useNativeEvent } from 'shared/src/hooks';
import { ScrollContext } from '../../../../../context/ScrollContext';
import { useGoodsSno } from '../../../../../hooks/useGoodsSno';
import { parseGoodsSnoFromDeeplink } from '../../../../../utils/parseGoodsSnoFromDeeplink';

/**
 * 가격 롤링 트리거. 대상 요소가 화면에 100% 노출되고 스크롤이 멈춘(idle) 시점에 true.
 * latch는 진입(cold mount 또는 warm 재진입)당 1회.
 * - 나갔다 재진입: cold면 새 mount, warm이면 incomingDeeplink로 처리 → 재생 O
 *   · 이미 재생됨: latch 리셋 + play false→true 재재생
 *   · 미재생: 노출 재판정만
 * - 위에 화면 쌓았다 복귀: 신호 없음 → 재생 X
 * @param enabled false면 노출 판정을 건너뛴다(대조군·비프로모션은 롤링 자체가 없으므로 측정 불필요).
 */
export const usePriceRollingTrigger = (
  ref: RefObject<View | null>,
  enabled: boolean,
): { shouldPlay: boolean; onLayout: () => void } => {
  const { isScrolling, listRef } = useContext(ScrollContext);
  const instanceId = useInstanceId();
  const goodsSno = useGoodsSno();
  const [shouldPlay, setShouldPlay] = useState(false);
  const playedRef = useRef(false);

  // 노출 판정 기준 영역(window 좌표). 하단 도킹 CTA 블록(배너+버튼+세이프에어리어)은
  // 리스트 밖(아래 형제)에 쌓이므로, 리스트 자체 bounds가 실제 노출 가능한 영역이다
  // 계측 불가 시 window 전체로 폴백 —
  // 폴백에서는 CTA 뒤에 가려진 칩도 노출로 오판할 수 있으나 기존 동작과 동일한 수준.
  const measureViewport = useCallback(
    (onMeasured: (top: number, bottom: number) => void) => {
      // 타입상 ScrollViewComponent에는 measureInWindow가 없지만 네이티브 호스트
      // 컴포넌트는 런타임에 지원한다. 미지원 환경(web 등)은 함수 체크로 폴백.
      const scrollNode = listRef?.current?.getNativeScrollRef?.() as
        | Partial<Pick<View, 'measureInWindow'>>
        | null
        | undefined;
      if (typeof scrollNode?.measureInWindow === 'function') {
        scrollNode.measureInWindow((_x, y, _width, height) =>
          onMeasured(y, y + height),
        );
        return;
      }
      onMeasured(0, Dimensions.get('window').height);
    },
    [listRef],
  );

  const checkVisibility = useCallback(() => {
    if (!enabled || playedRef.current || !ref.current) return;
    // mount raf / onLayout에서도 scroll idle일 때만 재생 (스크롤 중 조기 latch 방지)
    if (isScrolling?.value) return;
    ref.current.measureInWindow((_x, y, _width, height) => {
      if (height === 0) return;
      // measure 콜백 사이 스크롤이 시작됐을 수 있음
      if (isScrolling?.value) return;
      measureViewport((viewportTop, viewportBottom) => {
        if (playedRef.current || isScrolling?.value) return;
        // 요소 상·하단이 모두 노출 영역 안 = 100% 노출
        if (y >= viewportTop && y + height <= viewportBottom) {
          playedRef.current = true;
          setShouldPlay(true);
        }
      });
    });
  }, [ref, enabled, measureViewport, isScrolling]);

  // 스크롤 정지(true→false) 시점마다 노출 판정
  useAnimatedReaction(
    () => isScrolling?.value ?? false,
    (scrolling, previous) => {
      if (previous && !scrolling) {
        scheduleOnRN(checkVisibility);
      }
    },
    [checkVisibility],
  );

  // 초기 진입: 스크롤 없이 이미 노출된 경우 (레이아웃 완료 후 1프레임)
  useEffect(() => {
    if (!enabled) return;
    const raf = requestAnimationFrame(checkVisibility);
    return () => cancelAnimationFrame(raf);
  }, [checkVisibility, enabled]);

  // warm 서피스 재진입: mount가 유지되므로 딥링크 이벤트로 진입을 감지한다.
  // - 이미 재생됨: latch 리셋 + play false→true로 재재생 (RollingNumberText 마운트 유지)
  // - 미재생: play 토글 없이 노출 재판정만
  useNativeEvent('incomingDeeplink', instanceId, ({ incomingDeeplink }) => {
    if (parseGoodsSnoFromDeeplink(incomingDeeplink) !== goodsSno) return;
    if (playedRef.current) {
      playedRef.current = false;
      setShouldPlay(false);
    }
    // 전환 애니메이션 중 조기 재생 방지 — idle 후 노출 재판정
    InteractionManager.runAfterInteractions(() => {
      requestAnimationFrame(checkVisibility);
    });
  });

  // onLayout: 칩 레이아웃 확정 시점에도 판정 (raf가 이른 케이스 보강)
  return { shouldPlay, onLayout: checkVisibility };
};
